import { types, cast } from 'mobx-state-tree';
import { ConversationModel, CustomerContext } from './models/ConversationModel';
import Tokens from './tokens/Tokens';
import { getDefaultClientAIContentUrl, getDefaultClientAIStreamUrl, } from './defaultClientAIUrls';
const parseArrayType = (value) => {
    if (!value)
        return [];
    if (typeof value === 'string') {
        // Split by comma and filter out empty/null values, then trim
        return value.split(',').map(item => item?.trim()).filter(item => item != null && item !== '');
    }
    if (Array.isArray(value)) {
        // Filter out null/undefined values and convert to strings
        return value
            .map(item => {
            if (item == null)
                return null;
            if (typeof item === 'string')
                return item.trim();
            return String(item).trim();
        })
            .filter((item) => item != null && item !== '');
    }
    return [];
};
const getLocaleFromCustomerContext = (customerContext) => (customerContext?.locale || customerContext?.currentPage?.locale);
const PAGE_CONTENT_REQUEST_TIMEOUT_MS = 65000;
const createTokensProvider = (portalTokens, defaultLocale, locale) => {
    const tokens = new Tokens({ tokens: portalTokens, defaultLocale });
    if (locale) {
        tokens.setLocale(locale);
    }
    return tokens;
};
/**
 * MobX-state-tree model for ConversationStore implementing IConversationStore
 */
export const ConversationStore = types
    .model('ConversationStore', {
    aiMode: types.optional(types.union(types.literal('search'), types.literal('shopper'), types.literal('inline')), 'search'),
    currentPortalID: types.optional(types.string, ''),
    currentOrganizationID: types.optional(types.string, ''),
    clientAIUrl: types.optional(types.string, ''),
    clientAIContentUrl: types.optional(types.string, ''),
    clientAIBaseUrl: types.optional(types.string, ''),
    currentConversation: types.maybe(ConversationModel),
    stylesheet: types.frozen(),
    promptPlaceholder: types.optional(types.string, ''),
    clientAIFeedbackButtons: types.optional(types.boolean, false),
    isAIServiceUnavailable: types.optional(types.boolean, false),
    clientAIConfig: types.frozen(),
    clientAISuggestedPrompts: types.optional(types.array(types.string), []),
    clientAISuggestedPromptsLocalized: types.optional(types.boolean, false),
    clientAISuggestedPromptsL10N: types.frozen(),
    clientAIStartingMessage: types.optional(types.string, ''),
    clientAIPersistentPrompts: types.optional(types.string, ''),
    clientAIPersistentPromptsLocalized: types.optional(types.boolean, false),
    clientAIPersistentPromptsL10N: types.frozen(),
    suggestedPromptsHeader: types.optional(types.string, ''),
    customerContext: types.maybe(CustomerContext),
    currentUser: types.frozen(),
    widgetProps: types.frozen(),
    widgetConfigId: types.optional(types.string, ''),
    amplifyEnvironment: types.optional(types.string, ''),
    suggestedPrompts: types.optional(types.array(types.string), []),
    smartSummary: types.optional(types.frozen(), null),
    isLoadingSuggestedPrompts: types.optional(types.boolean, false),
    portalTokens: types.frozen(),
    lastSuggestedPromptsKey: types.optional(types.string, ''),
    defaultLocale: types.optional(types.string, 'en'),
})
    .volatile(() => ({
    tokensProvider: createTokensProvider(undefined, 'en'),
    pageAdditionalDataRequests: new Map(),
}))
    .actions((self) => ({
    afterCreate() {
        const locale = getLocaleFromCustomerContext(self.customerContext);
        self.tokensProvider = createTokensProvider(self.portalTokens, self.defaultLocale, locale);
    },
    load(organization, portal, widgetProps, widgetConfigId, amplifyEnvironment) {
        // Helper function to safely assign string values, preventing null assignments
        const safeString = (value, fieldName, defaultValue = '') => {
            if (value === null || value === undefined) {
                console.warn(`[ConversationStore] Warning: ${fieldName} is null/undefined, using default: "${defaultValue}"`);
                return defaultValue;
            }
            if (typeof value !== 'string') {
                console.warn(`[ConversationStore] Warning: ${fieldName} is not a string (type: ${typeof value}), converting to string`);
                return String(value);
            }
            return value;
        };
        try {
            const env = typeof amplifyEnvironment === 'string' ? amplifyEnvironment.trim() : '';
            if (env === '') {
                throw new Error('amplifyEnvironment is required to load ConversationStore');
            }
            self.amplifyEnvironment = env;
            self.currentOrganizationID = safeString(organization?.id, 'organization.id');
            self.currentPortalID = safeString(portal?.id, 'portal.id');
            self.clientAIUrl = getDefaultClientAIStreamUrl(env);
            {
                const derivedContent = getDefaultClientAIContentUrl(env);
                const rawContentUrl = organization?.clientAIContentUrl;
                self.clientAIContentUrl =
                    rawContentUrl != null && rawContentUrl !== ''
                        ? typeof rawContentUrl === 'string'
                            ? rawContentUrl
                            : String(rawContentUrl)
                        : derivedContent;
            }
            self.clientAIBaseUrl = safeString(portal?.clientAIBaseUrl, 'portal.clientAIBaseUrl');
            self.stylesheet = portal.stylesheet;
            // TODO: remove this MCN-2144 (move to tokens)
            self.promptPlaceholder = safeString(portal?.clientAIPromptPlaceholder, 'portal.clientAIPromptPlaceholder');
            self.clientAIFeedbackButtons = portal.clientAIFeedbackButtons;
            self.isAIServiceUnavailable = organization.isAIServiceUnavailable;
            self.clientAIConfig = portal.clientAIConfig;
            // TODO: remove this MCN-2144 (move to tokens)
            try {
                const parsedPrompts = parseArrayType(portal.clientAISuggestedPrompts);
                // Validate that all array elements are strings (no null/undefined)
                const invalidIndices = parsedPrompts
                    .map((item, index) => (item == null || typeof item !== 'string' ? index : -1))
                    .filter(index => index !== -1);
                if (invalidIndices.length > 0) {
                    console.warn(`[ConversationStore] Warning: clientAISuggestedPrompts contains invalid values at indices: ${invalidIndices.join(', ')}`);
                }
                self.clientAISuggestedPrompts = cast(parsedPrompts);
            }
            catch (error) {
                console.error('[ConversationStore] Error parsing clientAISuggestedPrompts:', {
                    error: error.message,
                    value: portal.clientAISuggestedPrompts,
                    type: typeof portal.clientAISuggestedPrompts,
                    isArray: Array.isArray(portal.clientAISuggestedPrompts),
                });
                // Fallback to empty array if parsing fails
                self.clientAISuggestedPrompts = cast([]);
            }
            // Read from clientAIConfig with fallback to legacy fields
            self.clientAISuggestedPromptsLocalized = portal.clientAIConfig?.clientAISuggestedPromptsLocalized
                ?? portal.clientAISuggestedPromptsLocalized
                ?? false;
            self.clientAISuggestedPromptsL10N = portal.clientAIConfig?.clientAISuggestedPromptsL10N
                ?? portal.clientAISuggestedPromptsL10N;
            // TODO: remove this MCN-2144 (move to tokens)
            self.clientAIStartingMessage = safeString(portal?.clientAIStartingMessage, 'portal.clientAIStartingMessage');
            // TODO: remove this MCN-2144 (move to tokens)
            self.clientAIPersistentPrompts = safeString(portal?.clientAIPersistentPrompts, 'portal.clientAIPersistentPrompts');
            // Read from clientAIConfig with fallback to legacy fields
            self.clientAIPersistentPromptsLocalized = portal.clientAIConfig?.clientAIPersistentPromptsLocalized
                ?? portal.clientAIPersistentPromptsLocalized
                ?? false;
            self.clientAIPersistentPromptsL10N = portal.clientAIConfig?.clientAIPersistentPromptsL10N
                ?? portal.clientAIPersistentPromptsL10N;
            // suggestedPromptsHeader will be resolved in components using resolvePortalText
            self.suggestedPromptsHeader = safeString(portal?.clientAIConfig?.pageSuggestedPromptsHeader, 'portal.clientAIConfig.pageSuggestedPromptsHeader');
            self.widgetProps = widgetProps;
            self.widgetConfigId = widgetConfigId || '';
            const defaultLocale = portal.defaultLocale || 'en';
            self.defaultLocale = safeString(defaultLocale, 'portal.defaultLocale', 'en');
            self.portalTokens = portal.tokens;
            const locale = getLocaleFromCustomerContext(self.customerContext);
            self.tokensProvider = createTokensProvider(self.portalTokens, defaultLocale, locale);
        }
        catch (error) {
            // Enhanced error logging to identify the problematic field
            console.error('[ConversationStore] Error in load method:', {
                error: error.message,
                stack: error.stack,
                organizationId: organization?.id,
                portalId: portal?.id,
                organizationKeys: organization ? Object.keys(organization) : null,
                portalKeys: portal ? Object.keys(portal) : null,
            });
            throw new Error(`Failed to load ConversationStore: ${error.message}. This usually means a null/undefined value was assigned to a string field. Check the logs above for warnings about specific fields.`);
        }
    },
    /**
     * Append data to the customer context
     */
    appendCustomerContext(customerContext) {
        if (!self.customerContext) {
            const initialLocale = customerContext?.locale || customerContext?.currentPage?.locale;
            self.customerContext = CustomerContext.create({
                currentPage: customerContext?.currentPage,
                locale: initialLocale,
                currency: customerContext?.currency || customerContext?.currentPage?.currency || 'USD',
                isLoggedIn: customerContext?.isLoggedIn !== undefined
                    ? customerContext.isLoggedIn : false,
            });
        }
        else {
            self.customerContext.setCurrentPage(customerContext);
            // Preserve isLoggedIn if explicitly provided
            if (customerContext?.isLoggedIn !== undefined) {
                self.customerContext.setLoggedIn(customerContext.isLoggedIn);
            }
            // Update locale if provided
            if (customerContext?.locale || customerContext?.currentPage?.locale) {
                const locale = customerContext?.locale || customerContext?.currentPage?.locale;
                self.customerContext.setLocale(locale);
            }
            // Update currency if provided
            if (customerContext?.currency || customerContext?.currentPage?.currency) {
                const currency = customerContext?.currency || customerContext?.currentPage?.currency;
                self.customerContext.setCurrency(currency);
            }
        }
        // Geolocation from host (only passed when portal has enableGeoLocation and shopper consented).
        // Shape: { geoLocation: { lat, lng } } or explicit null to clear.
        if (customerContext && 'geoLocation' in customerContext) {
            const geo = customerContext.geoLocation;
            if (geo && typeof geo.lat === 'number' && typeof geo.lng === 'number') {
                self.customerContext?.setGeoLocation({ lat: geo.lat, lng: geo.lng });
            }
            else if (geo === null) {
                self.customerContext?.setGeoLocation(null);
            }
        }
        const locale = getLocaleFromCustomerContext(self.customerContext);
        self.tokensProvider.setLocale(locale || self.defaultLocale);
        // console.log('[ConversationStore] Updated customerContext:', self.customerContext);
    },
    /**
     * Create a new conversation and set as current
     */
    createConversation(snapshot) {
        const conversation = ConversationModel.create({
            ...snapshot,
            id: snapshot.id || Math.random().toString(36).substr(2, 9),
            createdAt: snapshot.createdAt ? new Date(snapshot.createdAt) : new Date(),
            updatedAt: snapshot.updatedAt ? new Date(snapshot.updatedAt) : new Date(),
            messages: snapshot?.messages || [],
        });
        self.currentConversation = conversation;
        return conversation;
    },
    /**
     * Set the current conversation
     */
    setCurrentConversation(newConversation) {
        self.currentConversation = newConversation;
        return newConversation;
    },
    getOrLoad() {
        // console.log('>>>>>>>>>>>>> getOrLoad', type, id);
        return undefined;
    },
    /**
     * Set suggested prompts from AI
     */
    setSuggestedPrompts(prompts) {
        // Filter out null/undefined values and ensure all are strings
        const safePrompts = (prompts || [])
            .map(item => {
            if (item == null)
                return null;
            if (typeof item === 'string')
                return item.trim();
            return String(item).trim();
        })
            .filter((item) => item != null && item !== '');
        self.suggestedPrompts = cast(safePrompts);
    },
    setIsLoadingSuggestedPrompts(isLoading) {
        self.isLoadingSuggestedPrompts = isLoading;
    },
    setSmartSummary(summary) {
        self.smartSummary = summary;
    },
    setLastSuggestedPromptsKey(pageKey) {
        self.lastSuggestedPromptsKey = pageKey;
    },
})).actions((self) => ({
    requestPageAdditionalData: (location) => {
        const { customerContext } = self;
        // Helper function to detect product page using regex/match
        const isProductPageByPath = () => {
            if (typeof window === 'undefined')
                return false;
            const productPagePath = self.clientAIConfig?.productPagePath || '/product';
            const escapedPath = productPagePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const productRegex = new RegExp(`${escapedPath}(?:s)?/([^/?]+)`, 'i');
            return productRegex.test(window.location.pathname);
        };
        const isProduct = customerContext?.currentPage?.isProduct || isProductPageByPath();
        const isCollection = Boolean(customerContext?.currentPage?.isCollection) && !isProduct;
        if (!isProduct && !isCollection) {
            // console.log('Page is not a product or collection page, skipping page-specific prompts');
            return Promise.resolve();
        }
        const pageKey = isProduct
            ? (customerContext?.currentPage?.product?.id
                || customerContext?.currentPage?.product?.handle
                || customerContext?.currentPage?.url
                || customerContext?.currentPage?.title
                || location)
            : (customerContext?.currentPage?.collection?.id
                || customerContext?.currentPage?.collection?.handle
                || customerContext?.currentPage?.url
                || customerContext?.currentPage?.title
                || location);
        if (pageKey && self.lastSuggestedPromptsKey === pageKey) {
            return Promise.resolve();
        }
        const requestKey = String(pageKey || '').trim();
        if (!requestKey) {
            return Promise.resolve();
        }
        const inFlightRequest = self.pageAdditionalDataRequests.get(requestKey);
        if (inFlightRequest) {
            return inFlightRequest;
        }
        const pageUrl = String(customerContext?.currentPage?.url || location || '').trim();
        if (!pageUrl) {
            return Promise.resolve();
        }
        try {
            // eslint-disable-next-line no-new
            new URL(pageUrl);
        }
        catch {
            return Promise.resolve();
        }
        const request = (async () => {
            const abortController = typeof AbortController !== 'undefined'
                ? new AbortController()
                : null;
            const timeoutID = abortController
                ? setTimeout(() => abortController.abort(), PAGE_CONTENT_REQUEST_TIMEOUT_MS)
                : null;
            try {
                self.setIsLoadingSuggestedPrompts(true);
                const base = (self.clientAIContentUrl || self.clientAIUrl).replace(/\/?$/, '/');
                const params = new URLSearchParams();
                params.set('organizationID', self.currentOrganizationID);
                params.set('portalID', self.currentPortalID);
                params.set('pageUrl', pageUrl);
                const loc = getLocaleFromCustomerContext(customerContext);
                if (loc) {
                    params.set('locale', loc);
                }
                if (isProduct) {
                    params.set('isProduct', '1');
                }
                if (isCollection) {
                    params.set('isCollection', '1');
                }
                const getUrl = `${base}?${params.toString()}`;
                const res = await fetch(getUrl, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                    },
                    ...(abortController ? { signal: abortController.signal } : {}),
                });
                if (!res.ok) {
                    throw new Error(`Page content request failed: ${res.status}`);
                }
                const parsed = await res.json();
                if (parsed?.suggestedPrompts) {
                    self.setSuggestedPrompts(parsed.suggestedPrompts);
                }
                if (parsed?.smartSummary) {
                    self.setSmartSummary(parsed.smartSummary);
                }
                self.setLastSuggestedPromptsKey(requestKey);
            }
            catch (err) {
                console.error('Error requesting page additional data:', err);
            }
            finally {
                if (timeoutID !== null) {
                    clearTimeout(timeoutID);
                }
                self.pageAdditionalDataRequests.delete(requestKey);
                self.setIsLoadingSuggestedPrompts(self.pageAdditionalDataRequests.size > 0);
            }
        })();
        self.pageAdditionalDataRequests.set(requestKey, request);
        return request;
    },
    requestPageSpecificPrompts(location) {
        return self.requestPageAdditionalData(location);
    },
}))
    .views((self) => ({
    get tokens() {
        return self.tokensProvider;
    },
}));
//# sourceMappingURL=ConversationStore.js.map